import React, { useContext, useState } from "react";
import UsersContext from "../Context/UsersContext";
import DataContext from "../Context/DAtaContext";
import supabase from "../Config/Supabase";
import { toast } from "react-toastify";
import { Loader, Package } from "lucide-react";
import { BsFillTrash3Fill } from "react-icons/bs";
import { AnimatePresence, motion } from "framer-motion";
import { MdOutlineFileUpload } from "react-icons/md";
import { FaPenToSquare } from "react-icons/fa6";
import { Trash2 } from "lucide-react";
import {
  UserCheck,
  PlusCircle,
  ChevronDown,
  MapPin,
  Plus,
  Users,
  User,
} from "lucide-react";

const { v4: uuidv4 } = require("uuid");

/* ─────────────────────────────────────────────
   Small reusable field-error component
───────────────────────────────────────────── */
const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-8 text-center border border-gray-300 border-dashed rounded-lg dark:border-gray-700">
    <Package className="w-6 h-6 text-gray-300 dark:text-gray-600" />
    <p className="text-xs text-gray-400 dark:text-gray-500">{text}</p>
  </div>
);

const FieldError = ({ show, message }) =>
  show && message ? (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-1 mt-1 text-xs text-red-400"
    >
      <span className="inline-block w-1 h-1 bg-red-400 rounded-full" />
      {message}
    </motion.p>
  ) : null;

const FieldLabel = ({ children }) => (
  <label className="block mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">
    {children}
  </label>
);

/* ─────────────────────────────────────────────
   Labelled field wrapper
───────────────────────────────────────────── */
const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold tracking-widest text-gray-400 uppercase dark:text-gray-500">
      {label}
    </label>
    {children}
  </div>
);
const inputCls =
  "w-full min-h-11 px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500";

const selectCls = `${inputCls} appearance-none cursor-pointer pr-10`;

const inputClass =
  "w-full min-h-11 px-3.5 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl outline-none text-sm text-gray-800 dark:text-gray-100 placeholder:text-xs md:placeholder:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500 focus:ring-4 focus:ring-green-500/10 focus:border-green-500";

const primaryBtnClass =
  "inline-flex items-center justify-center gap-2 min-h-11 w-full sm:w-auto sm:min-w-[190px] px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-xl shadow-sm outline-none transition-all duration-200 hover:bg-green-600 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0";

const SectionHeader = ({ title, accent = "emerald", icon: Icon }) => (
  <div className="flex">
    {Icon && (
      <div
        className={`
          flex justify-center
          w-10 h-10 rounded-xl shrink-0
        `}
      >
        <Icon size={20} />
      </div>
    )}

    <div className="min-w-0">
      <h2 className="text-sm font-bold tracking-wide text-gray-800 uppercase dark:text-gray-100">
        {title}
      </h2>

      <div className={`mt-1 h-0.5 w-8 rounded-full bg-green-600`} />
    </div>
  </div>
);

const Section = ({ title, icon, accent = "emerald", children }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="mb-6"
  >
    <SectionHeader title={title} icon={icon} accent={accent} />

    <div className="grid grid-cols-1 gap-4 p-4 bg-white border border-gray-200 shadow-sm sm:p-5 md:grid-cols-2 xl:grid-cols-3 dark:bg-gray-900 dark:border-gray-800 rounded-2xl">
      {children}
    </div>
  </motion.section>
);

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="mb-6 overflow-hidden bg-white border border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-800 rounded-2xl"
  >
    <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-100 sm:px-5 dark:border-gray-800">
      <div className="flex items-center min-w-0 gap-3">
        {Icon && (
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-50 dark:bg-green-500/10 shrink-0">
            <Icon className="w-4 h-4 text-green-600 dark:text-green-500" />
          </div>
        )}

        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-wide text-gray-700 uppercase dark:text-gray-200">
            {title}
          </h3>

          {subtitle && (
            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className="p-4 sm:p-5">{children}</div>
  </motion.section>
);

const UpdateFarmer = ({ farmer }) => {
  // Data imports from context
  const { vegetableBundle, Villages } = useContext(DataContext);
  const { fetchFarmerBundle, farmerbundle } = useContext(DataContext);
  const { fetchFarmers } = useContext(UsersContext);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [loading4, setLoading4] = useState(false);
  //
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Set initial state to farmer's existing data for updating
  const [farmerData, setFarmerData] = useState({
    id: farmer?.id,
    uuid: farmer?.uuid,
    name: farmer?.name,
    gender: farmer?.gender,
    village_id: farmer?.village_id,
    longitude: farmer?.longitude,
    latitude: farmer?.latitude,
    phone: farmer?.phone,
    belongs_in_group: farmer?.belongs_in_group,
    number_of_people_in_house_hold: farmer?.number_of_people_in_house_hold,
    number_of_children_under_5: farmer?.number_of_children_under_5,
    group_membership_status: farmer?.group_membership_status,
    number_of_breast_feeding_children:
      farmer?.number_of_breast_feeding_children,
    group_name: farmer?.group_name,
    creator_id: farmer?.creator_id,
    prefered_bundle: farmer?.prefered_bundle,
    device_id: farmer?.device_id,
    created_at: farmer?.created_at,
    updated_at: new Date().toISOString(),
  });

  /* ── Validation ─────────────────────────────────────────── */
  const validateField = (name, value, allData = farmerData) => {
    switch (name) {
      case "name":
        if (!value || value.trim().length < 2)
          return "Full name must be at least 2 characters";
        if (!/^[a-zA-Z\s]+$/.test(value))
          return "Name should only contain letters and spaces";
        return "";
      case "village_id":
        return !value ? "Please select a village" : "";
      case "phone":
        if (!value || value.length < 10)
          return "Please enter a valid phone number";
        if (!value || value.length >= 14)
          return "Please enter a valid phone number";
        if (!value.startsWith("+255")) return "Phone must start with +255";
        return "";
      case "number_of_people_in_house_hold":
        if (!value || parseInt(value) < 1) return "Must be at least 1";
        if (parseInt(value) > 50) return "Number seems too high, please verify";
        return "";
      case "number_of_children_under_5":
        if (value && parseInt(value) < 0) return "Cannot be negative";
        if (
          value &&
          allData.number_of_people_in_house_hold &&
          parseInt(value) > parseInt(allData.number_of_people_in_house_hold)
        )
          return "Cannot exceed total household members";
        return "";
      case "number_of_breast_feeding_children":
        if (value && parseInt(value) < 0) return "Cannot be negative";
        if (
          value &&
          allData.number_of_children_under_5 &&
          parseInt(value) > parseInt(allData.number_of_children_under_5)
        )
          return "Cannot exceed children under 5";
        return "";
      case "group_name":
        if (allData.belongs_in_group && (!value || value.trim().length < 2))
          return "Group name required (min 2 characters)";
        return "";
      case "group_membership_status":
        if (allData.belongs_in_group && !value)
          return "Please select a group position";
        return "";
      default:
        return "";
    }
  };

  /* ── Form handlers ───────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFarmerData({ ...farmerData, [name]: newValue });
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, newValue, {
      ...farmerData,
      [name]: newValue,
    });
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Phone handler — enforces +255 prefix
  const handlePhoneChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith("+255")) val = "+255" + val.replace(/^\+?255?/, "");
    setFarmerData({ ...farmerData, phone: val });
    setTouched((prev) => ({ ...prev, phone: true }));
    const error = validateField("phone", val, { ...farmerData, phone: val });
    setErrors((prev) => ({ ...prev, phone: error }));
  };

  // handle farmer update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update farmer data
      const { error } = await supabase
        .from("farmer")
        .update({
          name: farmerData.name,
          gender: farmerData.gender,
          village_id: farmerData.village_id,
          longitude: farmerData.longitude,
          latitude: farmerData.latitude,
          phone: farmerData.phone,
          belongs_in_group: farmerData.belongs_in_group,
          number_of_people_in_house_hold:
            +farmerData.number_of_people_in_house_hold,
          number_of_children_under_5: +farmerData.number_of_children_under_5,
          group_membership_status: farmerData.group_membership_status,
          number_of_breast_feeding_children:
            +farmerData.number_of_breast_feeding_children,
          group_name: farmerData.group_name,
          prefered_bundle: farmerData.prefered_bundle,
          device_id: farmerData.device_id,
          created_at: farmerData.created_at,
          updated_at: new Date().toISOString(),
        })
        .eq("id", farmerData.id)
        .select();

      // Check for errors
      if (error) throw error;

      // Re-fetch farmers to sync UI
      fetchFarmers();
      toast.success("Farmer updated successfully!");
    } catch (error) {
      console.error("Error updating farmer:", error.message);
      toast.error("Failed to update farmer: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getGPSLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFarmerData({
            ...farmerData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting location: " + error.message);
        },
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  // simple Logic

  // farmer bundles and vgbundles relationship
  const FBundle = farmerbundle?.filter((fb) => fb?.farmer_id === farmer?.uuid);

  const [rows, setRows] = useState([
    {
      id: uuidv4(),
      amount: 1,
      bundle_id: "",
      kifurushi: "",
      vilivyomo: "",
      farmer_id: farmerData?.uuid,
      device_id: farmerData?.device_id,
    },
  ]);

  const addRow = () => {
    const newRow = {
      id: uuidv4(),
      amount: 1,
      kifurushi: "",
      vilivyomo: "",
      bundle_id: "",
      farmer_id: farmer?.uuid,
      device_id: farmer?.device_id,
    };
    setRows([...rows, newRow]);
  };

  const deleteRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  // edit logic for preffered bunlde when user adds bundle
  const handleAddPackages = async () => {
    const CombineBundle = [...FBundle, ...rows];
    // Map through the remaining bundles and find corresponding vegetableBundle data
    const Vbundle = CombineBundle?.map((vf) => {
      // Find the related vegetable bundle by bundle_id
      const para = vegetableBundle?.find((vg) => vg.id === vf.bundle_id);

      // If para exists, return the mapped bundle information
      return {
        farmer_id: vf.farmer_id,
        amount: vf.amount,
        bundle_id: para?.id, // Make sure para exists before accessing id
        kifurushi: para?.name, // Same for name
        vilivyomo: para?.sub_bundles, // Same for sub_bundles
      };
    });

    // Flatten the array in case there are nested arrays
    const pasta = Vbundle?.flat();
    //
    const bundleInfo = pasta.map((dt) => {
      return `${dt.amount} ${dt.kifurushi} ${dt.vilivyomo || ""}`.trim();
    });

    // handle preffered bundle Update
    try {
      // Update farmer data
      const { error } = await supabase
        .from("farmer")
        .update({
          prefered_bundle: bundleInfo.join(", "),
          updated_at: new Date().toISOString(),
        })
        .eq("uuid", farmerData.uuid)
        .select();

      // Check for errors
      if (error) throw error;
      // Re-fetch farmers to sync UI
      fetchFarmers();
    } catch (error) {
      console.error("Error updating farmer:", error.message);
      toast.error("Failed to update Prefered bundle " + error.message);
    } finally {
    }
  };

  const handleBundleSubmit = async () => {
    try {
      // Prepare the data to be inserted
      const dataToInsert = rows.map((row) => ({
        id: row.id,
        amount: row.amount,
        farmer_id: row.farmer_id,
        bundle_id: row.bundle_id,
        device_id: row.device_id,
        created_at: new Date().toISOString(),
      }));

      // Insert the data
      const { error } = await supabase
        .from("farmer_bundle")
        .insert(dataToInsert)
        .select();

      // Check for errors from Supabase
      if (error) {
        console.error("Supabase Error:", error); // Log the full error object
        throw new Error(
          error.message || "Unknown error occurred while enrolling bundle.",
        ); // Use a fallback message
      }

      fetchFarmerBundle();
      toast.success("Bundled enrolled successfully!");
      setLoading2(false);

      // Reset the rows after successful submission
      setRows([
        {
          id: uuidv4(),
          amount: 1,
          bundle_id: "",
          kifurushi: "",
          vilivyomo: "",
          farmer_id: farmer?.uuid,
          device_id: farmer?.device_id,
        },
      ]);
    } catch (error) {
      console.error("Error enrolling bundle:", error.message || error);
      toast.error("Failed to enroll bundle.");
    }
  };

  const handleAddBundle = () => {
    handleAddPackages();
    handleBundleSubmit();
    setLoading2(true);
  };

  // Bundle Update
  const [rowz, setRowz] = useState({
    amount: 1,
    bundle_id: "",
    kifurushi: "",
    vilivyomo: "",
  });
  const [editingRow, setEditingRow] = useState(null);
  const [DeleteRow, setDeleteRow] = useState(null);

  const handleDeleteClick = (id, data) => {
    setDeleteRow(DeleteRow === id ? null : id);
    const VegBundle = vegetableBundle?.find((dt) => dt.id === data?.bundle_id);
    setRowz({
      amount: data.amount,
      bundle_id: data.bundle_id,
      kifurushi: VegBundle?.name,
      vilivyomo: VegBundle?.sub_bundles,
    });
  };
  const handleEditClick = (id, data) => {
    setEditingRow(editingRow === id ? null : id);
    const VegBundle = vegetableBundle?.find((dt) => dt.id === data?.bundle_id);
    setRowz({
      amount: data.amount,
      bundle_id: data.bundle_id,
      kifurushi: VegBundle?.name,
      vilivyomo: VegBundle?.sub_bundles,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRowz((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // handle preffered bundle Update from farmer
  const handlePBundle = async (id) => {
    // Filter out the bundle with the specified id from FBundle
    const removeB = FBundle?.filter((fb) => fb.id !== id);

    // Map through the remaining bundles and find corresponding vegetableBundle data
    const Vbundle = removeB?.map((vf) => {
      // Find the related vegetable bundle by bundle_id
      const para = vegetableBundle?.find((vg) => vg.id === vf.bundle_id);

      // If para exists, return the mapped bundle information
      return {
        farmer_id: vf.farmer_id,
        amount: vf.amount,
        bundle_id: para?.id, // Make sure para exists before accessing id
        kifurushi: para?.name, // Same for name
        vilivyomo: para?.sub_bundles, // Same for sub_bundles
      };
    });

    // Flatten the array in case there are nested arrays
    const pasta = Vbundle?.flat();

    // Combine the result with rowz and update state or further process
    const AddNew = [...pasta, rowz];

    //
    const bundleInfo = AddNew.map((dt) => {
      return `${dt.amount} ${dt.kifurushi} ${dt.vilivyomo || ""}`.trim();
    });

    // handle preffered bundle Update
    try {
      // Update farmer data
      const { error } = await supabase
        .from("farmer")
        .update({
          prefered_bundle: bundleInfo.join(", "),
          updated_at: new Date().toISOString(),
        })
        .eq("uuid", farmerData.uuid)
        .select();

      // Check for errors
      if (error) throw error;
      // Re-fetch farmers to sync UI
      fetchFarmers();
    } catch (error) {
      console.error("Error updating farmer:", error.message);
      toast.error("Failed to update Prefered bundle " + error.message);
    } finally {
    }
  };

  // handle bundle Edit Update
  const handleSaveChanges = async (rowId) => {
    try {
      const { error } = await supabase
        .from("farmer_bundle") // Replace with your actual table name
        .update({
          amount: rowz.amount,
          bundle_id: rowz.bundle_id,
        })
        .eq("id", rowId); // Update based on the row id

      if (error) {
        toast.error("Error updating bundle ");
        setLoading3(false);
        console.error("Error updating data: ", error);
      } else {
        fetchFarmerBundle();
        setLoading3(false);
        toast.success("Bundle updated successfully");
        setEditingRow(null);
      }
    } catch (err) {
      console.error("Error saving data: ", err);
    }
  };

  const handleBSubmit = (id) => {
    handlePBundle(id);
    handleSaveChanges(id);
    setLoading3(true);
  };

  // handle Farmer Packeg
  const handlePDel = async (id) => {
    const removeB = FBundle?.filter((fb) => fb.id !== id);

    // Map through the remaining bundles and find corresponding vegetableBundle data
    const Vbundle = removeB?.map((vf) => {
      // Find the related vegetable bundle by bundle_id
      const para = vegetableBundle?.find((vg) => vg.id === vf.bundle_id);

      // If para exists, return the mapped bundle information
      return {
        farmer_id: vf.farmer_id,
        amount: vf.amount,
        bundle_id: para?.id, // Make sure para exists before accessing id
        kifurushi: para?.name, // Same for name
        vilivyomo: para?.sub_bundles, // Same for sub_bundles
      };
    });

    const pasta = Vbundle?.flat();

    //
    const bundleInfo = pasta.map((dt) => {
      return `${dt.amount} ${dt.kifurushi} ${dt.vilivyomo || ""}`.trim();
    });

    // handle preffered bundle Update
    try {
      // Update farmer data
      const { error } = await supabase
        .from("farmer")
        .update({
          prefered_bundle: bundleInfo.join(", "),
          updated_at: new Date().toISOString(),
        })
        .eq("uuid", farmerData.uuid)
        .select();

      // Check for errors
      if (error) throw error;
      // Re-fetch farmers to sync UI
      fetchFarmers();
    } catch (error) {
      console.error("Error updating farmer:", error.message);
      toast.error("Failed to update Prefered bundle " + error.message);
    } finally {
    }
  };

  // delete Farmer bundle
  const handleDeletePackage = async (bundleId) => {
    try {
      const { error } = await supabase
        .from("farmer_bundle")
        .delete()
        .eq("id", bundleId);

      if (error) {
        toast.error("Error Deleting bundle ");
        setLoading4(false);
        console.error("Error updating data: ", error);
      } else {
        fetchFarmerBundle();
        setLoading4(false);
        toast.success("Bundle Deleted successfully");
        setDeleteRow(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // complete delete
  const handleDelete = (id) => {
    handlePDel(id);
    handleDeletePackage(id);
    setLoading4(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full py-4 dark:text-gray-300"
    >
      <div className="w-full max-w-[1500px] mx-auto">
        {/* =====================================================
          HEADER
        ====================================================== */}
        {/* Page header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="mb-1 text-xs font-semibold tracking-widest uppercase text-emerald-500">
              Enrollment
            </p>
            <h1 className="text-xl font-bold tracking-tight text-gray-900 md:text-2xl dark:text-white">
              Update Farmer
            </h1>
            <p></p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-xs bg-white dark:bg-gray-900 shrink-0">
            <span className="text-gray-400">ID</span>
            <span className="font-bold text-gray-700 dark:text-gray-200">
              #{farmerData.id}
            </span>
          </div>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* =====================================================
            PERSONAL INFORMATION
           ====================================================== */}
          <Section title="Personal Information" icon={User} accent="blue">
            <Field label="Full Name (Jina Kamili)">
              <input
                type="text"
                name="name"
                value={farmerData.name}
                onChange={handleChange}
                placeholder="e.g. Mhando Zuberi"
                className={inputCls}
              />

              <FieldError show={touched.name} message={errors.name} />
            </Field>

            <Field label="Gender (Jinsia)">
              <div className="relative">
                <select
                  name="gender"
                  value={farmerData.gender}
                  onChange={handleChange}
                  className={selectCls}
                >
                  <option value="male">Male (Mwanaume)</option>

                  <option value="female">Female (Mwanamke)</option>
                </select>

                <ChevronDown
                  size={15}
                  className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3.5 top-1/2"
                />
              </div>
            </Field>

            <Field label="Village (Kijiji)">
              <div className="relative">
                <select
                  name="village_id"
                  value={farmerData.village_id}
                  onChange={handleChange}
                  className={selectCls}
                >
                  <option value="" disabled>
                    Select Village
                  </option>

                  {Villages?.map((village) => (
                    <option key={village?.id} value={village?.id}>
                      {village?.name}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={15}
                  className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3.5 top-1/2"
                />
              </div>

              <FieldError
                show={touched.village_id}
                message={errors.village_id}
              />
            </Field>

            {/* Phone Number — custom input with +255 prefix */}
            <Field label="Phone Number (Namba ya Simu)">
              <div className="relative">
                <span className="absolute text-lg -translate-y-1/2 pointer-events-none select-none left-3 top-1/2 dark:text-gray-200">
                  🇹🇿
                </span>

                <input
                  type="tel"
                  name="phone"
                  value={farmerData.phone}
                  onChange={handlePhoneChange}
                  placeholder="+255 7XX XXX XXX"
                  className={`${inputCls} pl-10`}
                />
              </div>

              <FieldError show={touched.phone} message={errors.phone} />
            </Field>

            {/* GPS */}
            <Field label="GPS Location">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <motion.button
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  onClick={getGPSLocation}
                  className="inline-flex items-center justify-center gap-2 min-h-11 px-4 py-2.5 text-sm font-medium text-white transition-all bg-green-600 rounded-xl shrink-0"
                >
                  <MapPin size={15} />
                  Allow GPS
                </motion.button>

                {farmerData.latitude && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center px-3 min-h-11 rounded-xl bg-gray-50 dark:bg-gray-800"
                  >
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {Number(farmerData.latitude).toFixed(5)},{" "}
                      {Number(farmerData.longitude).toFixed(5)}
                    </span>
                  </motion.div>
                )}
              </div>
            </Field>
          </Section>

          {/* =====================================================
            GROUP MEMBERSHIP
          ====================================================== */}
          <Section title="Group Membership" icon={UserCheck} accent="violet">
            <div className="col-span-full">
              <label className="flex items-center justify-between gap-4 p-4 border border-gray-200 cursor-pointer rounded-xl dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="belongs_in_group"
                      checked={farmerData.belongs_in_group}
                      onChange={handleChange}
                      className="sr-only peer"
                    />

                    <div className="h-6 transition-colors bg-gray-200 rounded-full w-11 dark:bg-gray-700 peer-checked:bg-emerald-500" />

                    <div className="absolute w-5 h-5 transition-transform bg-white rounded-full shadow-sm top-0.5 left-0.5 peer-checked:translate-x-5" />
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Farmer belongs to a group
                    </p>

                    <p className="mt-0.5 text-xs text-gray-400">
                      Yupo kwenye Kikundi?
                    </p>
                  </div>
                </div>

                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    farmerData.belongs_in_group
                      ? "text-emerald-500"
                      : "text-gray-400"
                  }`}
                >
                  {farmerData.belongs_in_group ? "Active" : "Not Active"}
                </span>
              </label>
            </div>

            <AnimatePresence initial={false}>
              {farmerData.belongs_in_group && (
                <motion.div
                  initial={{
                    opacity: 0,
                    height: 0,
                    y: -8,
                  }}
                  animate={{
                    opacity: 1,
                    height: "auto",
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    height: 0,
                    y: -8,
                  }}
                  transition={{
                    duration: 0.3,
                  }}
                  className="grid grid-cols-1 gap-4 overflow-hidden col-span-full md:grid-cols-2"
                >
                  <Field label="Group Name (Jina la Kikundi)">
                    <input
                      type="text"
                      name="group_name"
                      value={farmerData.group_name}
                      onChange={handleChange}
                      placeholder="e.g. Kilimo Kwanza"
                      className={inputCls}
                    />

                    <FieldError
                      show={touched.group_name}
                      message={errors.group_name}
                    />
                  </Field>

                  <Field label="Group Position (Cheo chake)">
                    <div className="relative">
                      <select
                        name="group_membership_status"
                        value={farmerData.group_membership_status}
                        onChange={handleChange}
                        className={selectCls}
                      >
                        <option value="">Select Position</option>

                        <option value="mwenyekiti">Mwenyekiti</option>

                        <option value="katibu">Katibu</option>

                        <option value="mweka Hazina">Mweka Hazina</option>

                        <option value="mwana Chama">Mwana Chama</option>
                      </select>

                      <ChevronDown
                        size={15}
                        className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3.5 top-1/2"
                      />
                    </div>

                    <FieldError
                      show={touched.group_membership_status}
                      message={errors.group_membership_status}
                    />
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>
          </Section>

          {/* =====================================================
            HOUSEHOLD
          ====================================================== */}
          <Section title="Household Information" icon={Users} accent="amber">
            <Field label="Total People in Household (Idadi ya watu unao ishi nao)">
              <input
                type="number"
                name="number_of_people_in_house_hold"
                value={farmerData.number_of_people_in_house_hold}
                onChange={handleChange}
                placeholder="e.g. 5"
                min={1}
                className={inputCls}
              />

              <FieldError
                show={touched.number_of_people_in_house_hold}
                message={errors.number_of_people_in_house_hold}
              />
            </Field>

            <Field label="Children Under 5 (Watoto chini ya miaka 5)">
              <input
                type="number"
                name="number_of_children_under_5"
                value={farmerData.number_of_children_under_5}
                onChange={handleChange}
                placeholder="e.g. 2"
                min={0}
                className={inputCls}
              />

              <FieldError
                show={touched.number_of_children_under_5}
                message={errors.number_of_children_under_5}
              />
            </Field>

            <Field label="Breastfeeding Children (Watoto wanao nyonya)">
              <input
                type="number"
                name="number_of_breast_feeding_children"
                value={farmerData.number_of_breast_feeding_children}
                onChange={handleChange}
                placeholder="e.g. 1"
                min={0}
                className={inputCls}
              />

              <FieldError
                show={touched.number_of_breast_feeding_children}
                message={errors.number_of_breast_feeding_children}
              />
            </Field>
          </Section>

          {/* =====================================================
            UPDATE ACTION
          ====================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-end"
          >
            <motion.button
              whileHover={!loading ? { y: -2 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`${primaryBtnClass} w-full sm:w-auto`}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <MdOutlineFileUpload className="w-5 h-5" />
                  Update Farmer
                </>
              )}
            </motion.button>
          </motion.div>

          {/* =====================================================
            EXISTING BUNDLES
          ====================================================== */}
          <SectionCard
            icon={Package}
            title="Existing Bundles"
            subtitle="Vifurushi Vya Zamani"
          >
            {FBundle?.length ? (
              <div className="overflow-hidden border border-gray-200 rounded-2xl dark:border-gray-700">
                {/* =========================================================
                       DESKTOP TABLE
                 ========================================================== */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-xs font-semibold tracking-[0.12em] text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/70 dark:text-gray-400">
                        <th className="hidden w-16 px-4 py-3 font-semibold xl:table-cell">
                          SN
                        </th>
                        <th className="px-4 py-3 font-semibold">Kifurushi</th>
                        <th className="w-32 px-4 py-3 font-semibold">Amount</th>
                        <th className="hidden px-4 py-3 font-semibold xl:table-cell">
                          Vilivyomo
                        </th>
                        <th className="px-4 py-3 font-semibold text-left w-44">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <AnimatePresence mode="popLayout">
                        {FBundle?.map((row, index) => (
                          <React.Fragment key={index + row.id}>
                            <motion.tr
                              layout
                              initial={{
                                opacity: 0,
                                y: -10,
                              }}
                              animate={{
                                opacity: 1,
                                y: 0,
                              }}
                              exit={{
                                opacity: 0,
                                y: -10,
                              }}
                              transition={{
                                duration: 0.25,
                              }}
                              className="bg-white dark:bg-gray-900/50"
                            >
                              {/* Number */}
                              <td className="hidden px-4 py-4 align-middle xl:table-cell">
                                <span className="text-xs font-medium text-gray-400">
                                  {index + 1}
                                </span>
                              </td>

                              {/* Bundle */}
                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate dark:text-gray-200">
                                    {
                                      vegetableBundle?.find(
                                        (vg) => vg?.id === row?.bundle_id,
                                      )?.name
                                    }
                                  </p>
                                </div>
                              </td>

                              {/* Amount */}
                              <td className="px-4 py-4 align-middle">
                                <input
                                  type="number"
                                  className="w-24 px-3 py-2 text-sm outline-none min-h-10 bg-gray-50 rounded-xl dark:bg-gray-800 focus:ring-4 focus:ring-green-500/10 focus:border-green-500"
                                  value={row.amount}
                                  onChange={(e) =>
                                    setRowz(parseInt(e.target.value))
                                  }
                                />
                              </td>

                              {/* Contents */}
                              <td className="hidden px-4 py-4 align-middle xl:table-cell">
                                <input
                                  readOnly
                                  disabled
                                  type="text"
                                  className="w-full max-w-xs px-3 py-2 text-sm outline-none min-h-10 bg-gray-50 rounded-xl dark:bg-gray-800"
                                  placeholder="Vilivyomo"
                                  value={
                                    row[index]?.vilivyomo ||
                                    vegetableBundle?.find(
                                      (vg) => vg?.id === row?.bundle_id,
                                    )?.sub_bundles
                                  }
                                />
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-4 align-middle">
                                <div className="flex items-center justify-end gap-2">
                                  <motion.button
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.94 }}
                                    type="button"
                                    onClick={() => handleEditClick(row.id, row)}
                                    className="inline-flex items-center justify-center gap-2 px-3 text-xs font-medium text-blue-700 transition-colors min-h-10 bg-blue-50 rounded-xl dark:bg-blue-500/10 dark:text-blue-400"
                                  >
                                    <FaPenToSquare />
                                    <span>Edit</span>
                                  </motion.button>

                                  <motion.button
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.94 }}
                                    type="button"
                                    onClick={() =>
                                      handleDeleteClick(row.id, row)
                                    }
                                    className="inline-flex items-center justify-center gap-2 px-3 text-xs font-medium text-red-500 transition-colors min-h-10 bg-red-50 rounded-xl dark:bg-red-500/10"
                                  >
                                    <BsFillTrash3Fill />
                                    <span>Delete</span>
                                  </motion.button>
                                </div>
                              </td>
                            </motion.tr>

                            {/* =================================================
                              EDIT PANEL 
                            ======================================== */}
                            <AnimatePresence>
                              {editingRow === row.id && (
                                <motion.tr
                                  initial={{
                                    opacity: 0,
                                  }}
                                  animate={{
                                    opacity: 1,
                                  }}
                                  exit={{
                                    opacity: 0,
                                  }}
                                >
                                  <td
                                    colSpan={5}
                                    className="p-0 overflow-hidden bg-gray-50 dark:bg-gray-800/40"
                                  >
                                    <motion.div
                                      initial={{
                                        opacity: 0,
                                        height: 0,
                                      }}
                                      animate={{
                                        opacity: 1,
                                        height: "auto",
                                      }}
                                      exit={{
                                        opacity: 0,
                                        height: 0,
                                      }}
                                      transition={{
                                        duration: 0.3,
                                      }}
                                      className="overflow-hidden"
                                    >
                                      <div className="p-4 sm:p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                          <FaPenToSquare className="text-blue-500" />

                                          <span className="text-xs font-semibold tracking-wider text-gray-600 uppercase dark:text-gray-300">
                                            Edit Bundle
                                          </span>
                                        </div>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-3">
                                          <div>
                                            <FieldLabel>
                                              Change Bundle
                                            </FieldLabel>

                                            <select
                                              name="kifurushi"
                                              value={rowz.kifurushi || ""}
                                              onChange={(e) => {
                                                const selectedBundle =
                                                  vegetableBundle?.find(
                                                    (bundle) =>
                                                      bundle.name ===
                                                      e.target.value,
                                                  );

                                                setRowz({
                                                  ...rowz,
                                                  kifurushi: e.target.value,
                                                  bundle_id:
                                                    selectedBundle?.id || "",
                                                  vilivyomo:
                                                    selectedBundle?.sub_bundles ||
                                                    "",
                                                });
                                              }}
                                              className={`${inputClass} appearance-none`}
                                            >
                                              <option value="">
                                                Select Bundle
                                              </option>

                                              {vegetableBundle?.map(
                                                (bundle) => (
                                                  <option
                                                    key={bundle.id}
                                                    value={bundle.name}
                                                  >
                                                    {bundle.name}
                                                  </option>
                                                ),
                                              )}
                                            </select>
                                          </div>

                                          <div>
                                            <FieldLabel>Edit Amount</FieldLabel>

                                            <input
                                              name="amount"
                                              type="number"
                                              className={inputClass}
                                              value={rowz.amount}
                                              onChange={handleInputChange}
                                            />
                                          </div>

                                          <div className="flex items-end">
                                            {loading3 ? (
                                              <div className="flex items-center justify-center w-full min-h-11">
                                                <Loader className="w-5 h-5 text-gray-500 animate-spin dark:text-white" />
                                              </div>
                                            ) : (
                                              <motion.button
                                                whileHover={{
                                                  y: -1,
                                                }}
                                                whileTap={{
                                                  scale: 0.97,
                                                }}
                                                type="button"
                                                onClick={() =>
                                                  handleBSubmit(row.id)
                                                }
                                                className="inline-flex items-center justify-center w-full gap-2 px-4 text-sm font-medium text-white transition-all bg-blue-500 min-h-11 rounded-xl hover:bg-blue-600"
                                              >
                                                <MdOutlineFileUpload className="w-5 h-5" />
                                                Save Changes
                                              </motion.button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>

                            {/* =============================================
                                 DELETE CONFIRMATION
                             =============================================== */}
                            <AnimatePresence>
                              {DeleteRow === row.id && (
                                <motion.tr
                                  initial={{
                                    opacity: 0,
                                  }}
                                  animate={{
                                    opacity: 1,
                                  }}
                                  exit={{
                                    opacity: 0,
                                  }}
                                >
                                  <td
                                    colSpan={5}
                                    className="p-0 overflow-hidden bg-red-50 dark:bg-red-500/5"
                                  >
                                    <motion.div
                                      initial={{
                                        opacity: 0,
                                        height: 0,
                                      }}
                                      animate={{
                                        opacity: 1,
                                        height: "auto",
                                      }}
                                      exit={{
                                        opacity: 0,
                                        height: 0,
                                      }}
                                      className="overflow-hidden"
                                    >
                                      <div className="p-4 sm:p-5">
                                        <div className="flex items-start gap-3">
                                          <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-xl dark:bg-red-500/10 shrink-0">
                                            <Trash2 className="w-5 h-5 text-red-500" />
                                          </div>

                                          <div className="min-w-0">
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-gray-200">
                                              Delete Package
                                            </h4>

                                            <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                                              This action is permanent and
                                              cannot be undone. All associated
                                              data will be deleted.
                                            </p>
                                          </div>
                                        </div>

                                        <div className="p-3 mt-4 text-sm text-red-600 bg-white border border-red-100 rounded-xl dark:bg-gray-900 dark:border-red-500/10 dark:text-gray-300">
                                          Are you sure you want to delete this
                                          package?
                                          <span className="font-semibold">
                                            {" "}
                                            {rowz?.kifurushi} {rowz?.vilivyomo}
                                          </span>
                                        </div>

                                        <div className="flex flex-col gap-2 mt-4 sm:flex-row sm:justify-end">
                                          <button
                                            onClick={() => setDeleteRow(null)}
                                            type="button"
                                            className="w-full px-4 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-200 min-h-10 rounded-xl dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200 sm:w-auto hover:bg-gray-100 dark:hover:bg-gray-800"
                                          >
                                            Cancel
                                          </button>

                                          {loading4 ? (
                                            <button
                                              type="button"
                                              disabled
                                              className="flex items-center justify-center w-full px-4 bg-red-600 min-h-10 rounded-xl sm:w-auto"
                                            >
                                              <Loader className="w-4 h-4 text-white animate-spin" />
                                            </button>
                                          ) : (
                                            <motion.button
                                              whileTap={{
                                                scale: 0.97,
                                              }}
                                              type="button"
                                              onClick={() =>
                                                handleDelete(row.id)
                                              }
                                              className="inline-flex items-center justify-center w-full gap-2 px-4 text-sm font-medium text-white transition-colors bg-red-600 min-h-10 rounded-xl sm:w-auto hover:bg-red-700"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                              Delete Package
                                            </motion.button>
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>
                          </React.Fragment>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>

                {/* =========================================================
                  MOBILE VIEW
                ========================================================== */}
                <div className="divide-y divide-gray-100 md:hidden dark:divide-gray-800">
                  <AnimatePresence mode="popLayout">
                    {FBundle?.map((row, index) => (
                      <motion.div
                        key={index + row.id}
                        layout
                        initial={{
                          opacity: 0,
                          y: -10,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                          y: -10,
                        }}
                        transition={{
                          duration: 0.25,
                        }}
                        className="p-4 bg-white dark:bg-gray-900/50"
                      >
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div className="flex items-center min-w-0 gap-3">
                            <span className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg shrink-0 dark:bg-gray-800 dark:text-gray-400">
                              {index + 1}
                            </span>

                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                                Kifurushi
                              </p>

                              <p className="mt-1 text-sm font-semibold text-gray-700 truncate dark:text-gray-200">
                                {
                                  vegetableBundle?.find(
                                    (vg) => vg?.id === row?.bundle_id,
                                  )?.name
                                }
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <motion.button
                              whileTap={{ scale: 0.94 }}
                              type="button"
                              onClick={() => handleEditClick(row.id, row)}
                              className="inline-flex items-center justify-center text-blue-700 rounded-lg w-9 h-9 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400"
                            >
                              <FaPenToSquare />
                            </motion.button>

                            <motion.button
                              whileTap={{ scale: 0.94 }}
                              type="button"
                              onClick={() => handleDeleteClick(row.id, row)}
                              className="inline-flex items-center justify-center text-red-500 rounded-lg w-9 h-9 bg-red-50 dark:bg-red-500/10"
                            >
                              <BsFillTrash3Fill />
                            </motion.button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <span className="block mb-1 text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                              Amount
                            </span>

                            <input
                              type="number"
                              className="w-full px-3 py-2 text-sm outline-none min-h-10 bg-gray-50 rounded-xl dark:bg-gray-800 focus:ring-4 focus:ring-green-500/10 focus:border-green-500"
                              value={row.amount}
                              onChange={(e) =>
                                setRowz(parseInt(e.target.value))
                              }
                            />
                          </div>

                          <div>
                            <span className="block mb-1 text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                              Vilivyomo
                            </span>

                            <input
                              readOnly
                              disabled
                              type="text"
                              className="w-full px-3 py-2 text-sm outline-none min-h-10 bg-gray-50 rounded-xl dark:bg-gray-800"
                              placeholder="Vilivyomo"
                              value={
                                row[index]?.vilivyomo ||
                                vegetableBundle?.find(
                                  (vg) => vg?.id === row?.bundle_id,
                                )?.sub_bundles
                              }
                            />
                          </div>
                        </div>

                        {/* Mobile Edit Panel */}
                        <AnimatePresence>
                          {editingRow === row.id && (
                            <motion.div
                              initial={{
                                opacity: 0,
                                height: 0,
                              }}
                              animate={{
                                opacity: 1,
                                height: "auto",
                              }}
                              exit={{
                                opacity: 0,
                                height: 0,
                              }}
                              transition={{
                                duration: 0.3,
                              }}
                              className="overflow-hidden"
                            >
                              <div className="pt-4 mt-4 border-t border-gray-100 dark:border-gray-800">
                                <div className="flex items-center gap-2 mb-4">
                                  <FaPenToSquare className="text-blue-500" />

                                  <span className="text-xs font-semibold tracking-wider text-gray-600 uppercase dark:text-gray-300">
                                    Edit Bundle
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                  <div>
                                    <FieldLabel>Change Bundle</FieldLabel>

                                    <select
                                      name="kifurushi"
                                      value={rowz.kifurushi || ""}
                                      onChange={(e) => {
                                        const selectedBundle =
                                          vegetableBundle?.find(
                                            (bundle) =>
                                              bundle.name === e.target.value,
                                          );

                                        setRowz({
                                          ...rowz,
                                          kifurushi: e.target.value,
                                          bundle_id: selectedBundle?.id || "",
                                          vilivyomo:
                                            selectedBundle?.sub_bundles || "",
                                        });
                                      }}
                                      className={`${inputClass} appearance-none`}
                                    >
                                      <option value="">Select Bundle</option>

                                      {vegetableBundle?.map((bundle) => (
                                        <option
                                          key={bundle.id}
                                          value={bundle.name}
                                        >
                                          {bundle.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <FieldLabel>Edit Amount</FieldLabel>

                                    <input
                                      name="amount"
                                      type="number"
                                      className={inputClass}
                                      value={rowz.amount}
                                      onChange={handleInputChange}
                                    />
                                  </div>

                                  <div>
                                    <FieldLabel>Vilivyomo</FieldLabel>

                                    <input
                                      type="text"
                                      name="vilivyomo"
                                      className={inputClass}
                                      value={rowz?.vilivyomo}
                                      onChange={handleInputChange}
                                    />
                                  </div>

                                  <div>
                                    {loading3 ? (
                                      <div className="flex items-center justify-center w-full min-h-11">
                                        <Loader className="w-5 h-5 text-gray-500 animate-spin dark:text-white" />
                                      </div>
                                    ) : (
                                      <motion.button
                                        whileTap={{
                                          scale: 0.97,
                                        }}
                                        type="button"
                                        onClick={() => handleBSubmit(row.id)}
                                        className="inline-flex items-center justify-center w-full gap-2 px-4 text-sm font-medium text-white transition-all bg-blue-500 min-h-11 rounded-xl hover:bg-blue-600"
                                      >
                                        <MdOutlineFileUpload className="w-5 h-5" />
                                        Save Changes
                                      </motion.button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Mobile Delete Panel */}
                        <AnimatePresence>
                          {DeleteRow === row.id && (
                            <motion.div
                              initial={{
                                opacity: 0,
                                height: 0,
                              }}
                              animate={{
                                opacity: 1,
                                height: "auto",
                              }}
                              exit={{
                                opacity: 0,
                                height: 0,
                              }}
                              className="overflow-hidden"
                            >
                              <div className="pt-4 mt-4 border-t border-red-100 dark:border-red-500/10">
                                <div className="flex items-start gap-3">
                                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-xl dark:bg-red-500/10 shrink-0">
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                  </div>

                                  <div className="min-w-0">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-gray-200">
                                      Delete Package
                                    </h4>

                                    <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                                      This action is permanent and cannot be
                                      undone.
                                    </p>
                                  </div>
                                </div>

                                <div className="p-3 mt-4 text-sm text-red-600 bg-white border border-red-100 rounded-xl dark:bg-gray-900 dark:border-red-500/10 dark:text-gray-300">
                                  Are you sure you want to delete this package?
                                  <span className="font-semibold">
                                    {" "}
                                    {rowz?.kifurushi} {rowz?.vilivyomo}
                                  </span>
                                </div>

                                <div className="flex flex-col gap-2 mt-4">
                                  <button
                                    onClick={() => setDeleteRow(null)}
                                    type="button"
                                    className="w-full px-4 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-200 min-h-10 rounded-xl dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                                  >
                                    Cancel
                                  </button>

                                  {loading4 ? (
                                    <button
                                      type="button"
                                      disabled
                                      className="flex items-center justify-center w-full px-4 bg-red-600 min-h-10 rounded-xl"
                                    >
                                      <Loader className="w-4 h-4 text-white animate-spin" />
                                    </button>
                                  ) : (
                                    <motion.button
                                      whileTap={{
                                        scale: 0.97,
                                      }}
                                      type="button"
                                      onClick={() => handleDelete(row.id)}
                                      className="inline-flex items-center justify-center w-full gap-2 px-4 text-sm font-medium text-white transition-colors bg-red-600 min-h-10 rounded-xl hover:bg-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete Package
                                    </motion.button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <EmptyState text="No bundles enrolled for this farmer yet." />
            )}
          </SectionCard>

          {/* =====================================================
            ADD NEW BUNDLES
          ====================================================== */}
          <SectionCard
            icon={PlusCircle}
            title="Add New Bundles"
            subtitle="Ongeza Vifurushi"
          >
            <div className="overflow-hidden border border-gray-200 rounded-2xl dark:border-gray-700">
              {/* Desktop / Tablet Table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50 dark:bg-gray-800/60 dark:border-gray-700">
                    <tr>
                      <th className="w-16 hidden xl:table-cell px-4 py-3.5 text-xs  tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
                        #
                      </th>

                      <th className="px-4 py-3.5 text-xs  tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
                        Kifurushi
                      </th>

                      <th className="w-32 px-4 py-3.5 text-xs  tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
                        Amount
                      </th>

                      <th className="px-4 hidden xl:table-cell py-3.5 text-xs  tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
                        Vilivyomo
                      </th>

                      <th className="w-20 px-4 py-3.5 text-xs  tracking-wider text-center text-gray-500 uppercase dark:text-gray-400">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    <AnimatePresence mode="popLayout">
                      {rows?.map((row, index) => (
                        <motion.tr
                          layout
                          key={row.id}
                          initial={{
                            opacity: 0,
                            y: -10,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          exit={{
                            opacity: 0,
                            y: -10,
                          }}
                          className="transition-colors bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40"
                        >
                          {/* Number */}
                          <td className="hidden px-4 py-4 xl:table-cell">
                            <span className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                              {index + 1}
                            </span>
                          </td>

                          {/* Bundle */}
                          <td className="px-4 py-4">
                            <select
                              name="prefered_bundle"
                              value={rows[index]?.kifurushi || ""}
                              onChange={(e) => {
                                const selectedBundle = vegetableBundle?.find(
                                  (bundle) => bundle.name === e.target.value,
                                );

                                const newRows = [...rows];

                                newRows[index] = {
                                  ...newRows[index],
                                  kifurushi: e.target.value,
                                  bundle_id: selectedBundle?.id || "",
                                  vilivyomo: selectedBundle?.sub_bundles || "",
                                };

                                setRows(newRows);
                              }}
                              className={`${inputClass} appearance-none`}
                            >
                              <option value="">Select Bundle</option>

                              {vegetableBundle?.map((bundle) => (
                                <option key={bundle.id} value={bundle.name}>
                                  {bundle.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Amount */}
                          <td className="px-4 py-4">
                            <input
                              type="number"
                              className={inputClass}
                              value={row.amount}
                              onChange={(e) => {
                                const newRows = [...rows];

                                newRows[index].amount = parseInt(
                                  e.target.value,
                                );

                                setRows(newRows);
                              }}
                            />
                          </td>

                          {/* Contents */}
                          <td className="hidden px-4 py-4 xl:table-cell">
                            <FieldLabel>Vilivyomo</FieldLabel>

                            <input
                              type="text"
                              readOnly
                              className={`${inputClass} bg-gray-100 dark:bg-gray-700/70`}
                              placeholder="Vilivyomo"
                              value={rows[index]?.vilivyomo || ""}
                            />
                          </td>

                          {/* Delete */}
                          <td className="px-4 py-4 text-center">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() => deleteRow(row.id)}
                              className="inline-flex items-center justify-center w-10 h-10 text-red-500 transition-colors rounded-xl bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20"
                            >
                              <BsFillTrash3Fill />
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Mobile Layout */}
              <div className="block md:hidden">
                <AnimatePresence mode="popLayout">
                  {rows?.map((row, index) => (
                    <motion.div
                      layout
                      key={row.id}
                      initial={{
                        opacity: 0,
                        y: -10,
                        scale: 0.98,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                        scale: 0.98,
                      }}
                      className="p-4 bg-white border-b border-gray-200 last:border-b-0 dark:bg-gray-900 dark:border-gray-700"
                    >
                      {/* Mobile Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center justify-center w-4 h-4 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                            {index + 1}
                          </span>

                          <span className="text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400">
                            New Bundle
                          </span>
                        </div>

                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          type="button"
                          onClick={() => deleteRow(row.id)}
                          className="inline-flex items-center justify-center text-red-500 transition-colors w-9 h-9 rounded-xl bg-red-50 dark:bg-red-500/10 hover:bg-red-100"
                        >
                          <BsFillTrash3Fill />
                        </motion.button>
                      </div>

                      {/* Mobile Fields */}
                      <div className="space-y-4">
                        {/* Bundle */}
                        <div>
                          <FieldLabel>Kifurushi</FieldLabel>

                          <select
                            name="prefered_bundle"
                            value={rows[index]?.kifurushi || ""}
                            onChange={(e) => {
                              const selectedBundle = vegetableBundle?.find(
                                (bundle) => bundle.name === e.target.value,
                              );

                              const newRows = [...rows];

                              newRows[index] = {
                                ...newRows[index],
                                kifurushi: e.target.value,
                                bundle_id: selectedBundle?.id || "",
                                vilivyomo: selectedBundle?.sub_bundles || "",
                              };

                              setRows(newRows);
                            }}
                            className={`${inputClass} appearance-none`}
                          >
                            <option value="">Select Bundle</option>

                            {vegetableBundle?.map((bundle) => (
                              <option key={bundle.id} value={bundle.name}>
                                {bundle.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Amount */}
                        <div>
                          <FieldLabel>Amount</FieldLabel>

                          <input
                            type="number"
                            className={inputClass}
                            value={row.amount}
                            onChange={(e) => {
                              const newRows = [...rows];

                              newRows[index].amount = parseInt(e.target.value);

                              setRows(newRows);
                            }}
                          />
                        </div>

                        {/* Contents */}
                        <div className="hidden">
                          <FieldLabel>Vilivyomo</FieldLabel>

                          <input
                            type="text"
                            readOnly
                            className={`${inputClass} bg-gray-100 dark:bg-gray-700/70`}
                            placeholder="Vilivyomo"
                            value={rows[index]?.vilivyomo || ""}
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Empty State */}
              {rows?.length === 0 && (
                <div className="px-6 py-10 text-center bg-white dark:bg-gray-900">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No bundles added yet.
                  </p>
                </div>
              )}
            </div>

            {/* Add another row */}
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              type="button"
              onClick={addRow}
              className="inline-flex items-center justify-center gap-2 px-4 py-1 mt-4 text-sm font-medium text-green-700 transition-all min-h-10 bg-green-50 rounded-xl dark:bg-green-500/10 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-500/15"
            >
              <PlusCircle className="w-4 h-4" />
              Ongeza Bando
            </motion.button>
          </SectionCard>

          {/* =====================================================
            FINAL ACTION
          ====================================================== */}
          <div className="flex flex-col w-full md:items-end md:justify-end">
            <motion.button
              whileHover={!loading2 ? { y: -1 } : {}}
              whileTap={!loading2 ? { scale: 0.98 } : {}}
              onClick={handleAddBundle}
              type="button"
              disabled={loading2}
              className={primaryBtnClass}
            >
              {loading2 ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Adding Bundle...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Bundle
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default UpdateFarmer;
